import type { Site } from "@lumora/bridge";
import { useViewModel } from "@lumora/ui";
import { useEffect, useState } from "react";
import { useSiteOpsVm } from "./platformViewModels.js";
import { ownerSiteOrigin } from "./ownerSiteOrigin.js";
import { SiteAccountsList, StatusMessage } from "./SiteAccountsList.js";
import {
  DEFAULT_NEW_ACCOUNT_ROLE,
  primaryAccount,
  resetStatusMessage,
  SITE_ACCOUNT_ROLES,
} from "./siteAccounts.js";

export type OwnerProvisionPanelProps = {
  slug: string;
  site: Site | null;
};

/** Prefill create-owner email from Stripe Connect account email when present. */
export function connectOwnerEmailPrefill(site: Site | null): string {
  return site?.connectAccountEmail ?? "";
}

/** All accounts for a site: list, reset password, role change, add account, create owner. */
export function OwnerProvisionPanel({ slug, site }: OwnerProvisionPanelProps) {
  const vm = useSiteOpsVm();
  const { users, message: msg, error: err } = useViewModel(vm);
  const [email, setEmail] = useState(connectOwnerEmailPrefill(site));
  const [password, setPassword] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newRole, setNewRole] = useState<string>(DEFAULT_NEW_ACCOUNT_ROLE);

  useEffect(() => {
    void vm.loadUsers(slug);
  }, [vm, slug]);

  const hasOwner = primaryAccount(users) !== null;

  return (
    <section className="ops-panel platform-provision-card" data-owner-provision>
      <h3>Site accounts</h3>
      <p className="muted">
        Everyone who can sign in on this site slug. The owner subscribes and completes Stripe Connect
        when needed.
      </p>

      {users === null ? <p className="muted">Loading accounts…</p> : null}

      {users && users.length > 0 ? (
        <SiteAccountsList
          users={users}
          onReset={(u) => {
            const resetBaseUrl = ownerSiteOrigin({
              customDomain: site?.customDomain ?? "",
            });
            void vm.requestPasswordReset(slug, u.id, resetBaseUrl, resetStatusMessage);
          }}
          onRoleChange={(u, role) => {
            void vm.changeRole(slug, u.id, role);
          }}
        />
      ) : null}

      {users && !hasOwner ? (
        <form
          className="site-signin"
          aria-label="Create owner"
          onSubmit={(e) => {
            e.preventDefault();
            void vm.createOwner(slug, email, password).then((ok) => {
              if (ok) {
                setEmail("");
                setPassword("");
              }
            });
          }}
        >
          <label>
            Owner email
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </label>
          <label>
            Temporary password
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={12}
              autoComplete="new-password"
            />
          </label>
          <button type="submit" className="ops-btn">
            Create owner
          </button>
        </form>
      ) : null}

      {users ? (
        <form
          className="site-signin"
          aria-label="Add account"
          onSubmit={(e) => {
            e.preventDefault();
            void vm.createAccount(slug, newEmail, newPassword, newRole).then((ok) => {
              if (ok) {
                setNewEmail("");
                setNewPassword("");
              }
            });
          }}
        >
          <label>
            Account email
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              required
            />
          </label>
          <label>
            Temporary password
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={12}
              autoComplete="new-password"
            />
          </label>
          <label>
            Role
            <select value={newRole} onChange={(e) => setNewRole(e.target.value)}>
              {SITE_ACCOUNT_ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </label>
          <button type="submit" className="ops-btn">
            Add account
          </button>
        </form>
      ) : null}

      <StatusMessage msg={msg} err={err} />
    </section>
  );
}
